import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Share2, Copy, Users, Trophy, Target, Loader2, ArrowRight,
  Flame, Sparkles, Globe,
} from "lucide-react";

/*  THE WASNTME DETECTIVE SOCIETY — community layer landing page.
    Shows: your identity (Detective # + play history), this week's
    spotlight (auto-picked top performer, admin override wins), honest
    weekly stats, and a one-tap invite button.

    Zero-signup. Fingerprint-based. Reuses existing Detective # + plays.
*/
const Society = () => {
  const [me, setMe]             = useState(null);
  const [spotlight, setSpot]    = useState(null);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/society/me").then((r) => r.data).catch(() => null),
      api.get("/society/spotlight").then((r) => r.data).catch(() => null),
    ]).then(([m, s]) => {
      if (cancelled) return;
      setMe(m);
      setSpot(s?.spotlight || null);
      setStats(s?.stats || null);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const shareInvite = async () => {
    if (!me) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "WasntMe Detective Society",
          text:  me.share_text,
          url:   me.invite_url,
        });
      } else {
        await navigator.clipboard.writeText(`${me.share_text}\n${me.invite_url}`);
        toast.success("Invite copied — paste it anywhere.");
      }
    } catch {}
  };

  const copyLink = async () => {
    if (!me?.invite_url) return;
    try {
      await navigator.clipboard.writeText(me.invite_url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy blocked by this browser.");
    }
  };

  if (loading) {
    return (
      <div className="grain min-h-screen bg-[#0a0908] flex items-center justify-center text-[#a39797]">
        <Loader2 size={16} className="animate-spin" /> &nbsp; Loading the Society…
      </div>
    );
  }

  return (
    <div className="grain min-h-screen bg-[#0a0908] text-[#f4f0ec]">
      <div className="border-b border-[#2a2525] px-5 py-4 flex items-center justify-between">
        <Link to="/" className="text-[10px] tracking-[0.3em] text-[#d4af37]" data-testid="society-logo">
          ◉ WASNTME.PARTY
        </Link>
        <div className="text-[9px] tracking-[0.3em] text-[#5a5050]">DETECTIVE SOCIETY</div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-10">
        {/* Hero */}
        <div className="text-[10px] tracking-[0.3em] text-[#8b0000] mb-3">◉ THE WASNTME DETECTIVE SOCIETY</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-[1.05]" data-testid="society-headline">
          The case is open.<br/>
          The clues are waiting.<br/>
          <span className="text-[#d4af37]">But can you trust anybody?</span>
        </h1>
        <p className="text-sm text-[#a39797] mb-10 leading-relaxed max-w-xl">
          🌎 Meet detectives from around the world. 🔎 Test your instincts.
          🏆 Prove yourself case by case. 👥 Bring your crew and see who can solve the mystery.
          <span className="text-[#f4f0ec]"> Your story starts here.</span>
        </p>

        {/* YOUR identity card */}
        <div className="card-noir p-6 mb-7" data-testid="society-identity-card">
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">YOUR IDENTITY</div>
          {me?.has_identity ? (
            <>
              <div className="flex items-end gap-4 mb-4 flex-wrap">
                <div className="font-display text-[64px] leading-none text-[#f4f0ec]" data-testid="society-detective-number">
                  #{me.detective_number}
                </div>
                <div className="pb-2">
                  <div className="text-xs tracking-[0.2em] text-[#a39797]">DETECTIVE</div>
                  <div className="text-[10px] text-[#5a5050]">
                    Enlisted {me.assigned_at ? new Date(me.assigned_at).toLocaleDateString() : "recently"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5">
                <Stat label="CASES"   value={me.plays_total}   testid="society-stat-plays" />
                <Stat label="CRACKED" value={me.plays_correct} testid="society-stat-cracked" accent />
                <Stat label="ACCURACY" value={`${me.accuracy}%`} testid="society-stat-accuracy" />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={shareInvite} className="btn-primary text-sm" data-testid="society-invite-share">
                  <Share2 size={13} /> Invite your crew
                </button>
                <button onClick={copyLink} className="btn-ghost text-xs" data-testid="society-invite-copy">
                  <Copy size={11} /> Copy link
                </button>
                <Link to="/challenge" className="btn-ghost text-xs" data-testid="society-cta-challenge">
                  <Flame size={11} /> Run today's case
                </Link>
              </div>
            </>
          ) : (
            <div data-testid="society-no-identity">
              <p className="text-sm text-[#a39797] mb-4 leading-relaxed">
                You haven't been assigned a Detective # yet. Run the 60-Second Interrogation
                to lock in your identity — it's permanent, free, and takes a minute.
              </p>
              <Link to="/challenge" className="btn-primary text-sm" data-testid="society-cta-enlist">
                <Flame size={13} /> Enlist in the Society <ArrowRight size={12} />
              </Link>
            </div>
          )}
        </div>

        {/* Recent cases (only if any) */}
        {me?.plays_recent?.length > 0 && (
          <div className="card-noir p-5 mb-7" data-testid="society-recent-plays">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">YOUR LAST FEW CASES</div>
            <div className="space-y-1.5">
              {me.plays_recent.slice(0, 5).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-[#2a2525] last:border-0">
                  <div className="text-[#d4d0cc]">
                    {p.case_id?.replace(/_/g, " ")?.replace(/\b\w/g, c => c.toUpperCase()) || "Case"}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={p.correct ? "text-[#d4af37]" : "text-[#8b0000]"}>
                      {p.correct ? "CRACKED" : "WRONG"}
                    </span>
                    <span className="text-[#a39797]">{p.score}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* This week's spotlight */}
        <div className="card-noir p-6 mb-7" data-testid="society-spotlight-card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={13} className="text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
              THIS WEEK'S SPOTLIGHT
            </div>
            {spotlight?.source === "admin" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#d4af37]/40 text-[#d4af37] ml-1">
                CURATED
              </span>
            )}
          </div>
          {spotlight ? (
            <>
              <div className="font-display text-3xl mb-1 leading-tight" data-testid="society-spotlight-name">
                Detective <span className="text-[#d4af37]">#{spotlight.detective_number}</span>
              </div>
              {spotlight.source === "auto" ? (
                <div className="text-xs text-[#a39797] leading-relaxed">
                  {spotlight.correct} case{spotlight.correct !== 1 ? "s" : ""} cracked this week ·
                  score {spotlight.score}{spotlight.time_bank ? ` · ${spotlight.time_bank}s of total time banked` : ""}
                </div>
              ) : (
                <>
                  {spotlight.theory && (
                    <div className="text-sm text-[#d4d0cc] mt-3 leading-relaxed italic" data-testid="society-spotlight-theory">
                      "{spotlight.theory}"
                    </div>
                  )}
                  {spotlight.note && (
                    <div className="text-xs text-[#a39797] mt-2">{spotlight.note}</div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="text-sm text-[#a39797]" data-testid="society-spotlight-empty">
              This week's board is wide open. First honest crack takes the spotlight.
            </div>
          )}
        </div>

        {/* Weekly society stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="society-weekly-stats">
            <Stat label="DETECTIVES" value={stats.detectives_total}   testid="society-stat-total-detectives"  Icon={Users} />
            <Stat label="CASES PLAYED" value={stats.plays_this_week}   testid="society-stat-week-plays"       Icon={Target} />
            <Stat label="CRACKED"      value={stats.cracked_this_week} testid="society-stat-week-cracked"     Icon={Trophy} accent />
            <Stat label="ACCURACY"     value={`${stats.accuracy_this_week}%`} testid="society-stat-week-accuracy" Icon={Sparkles} />
          </div>
        )}

        {/* Call to action band */}
        <div className="card-noir p-6 mb-10" data-testid="society-cta-band">
          <div className="text-[10px] tracking-[0.3em] text-[#8b0000] mb-2">◉ YOUR MOVE</div>
          <h3 className="font-display text-2xl mb-2">Bring your crew. Prove yourselves.</h3>
          <p className="text-xs text-[#a39797] mb-4 leading-relaxed">
            Share your invite link. New detectives see your Detective # on their arrival.
            Then run a real case together with your crew.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {me?.has_identity && (
              <button onClick={shareInvite} className="btn-primary text-sm" data-testid="society-cta-share">
                <Share2 size={13} /> Share your invite
              </button>
            )}
            <Link to="/packs" className="btn-ghost text-sm" data-testid="society-cta-packs">
              Run a crew case <ArrowRight size={12} />
            </Link>
          </div>
        </div>

        {/* Hashtag footer */}
        <div className="text-center text-[10px] text-[#5a5050]" data-testid="society-tags">
          #WasntMeParty · #DetectiveSociety · #MysteryGame · #SocialDeduction · #GlobalMystery
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid, Icon, accent = false }) => (
  <div className="card-noir p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon size={10} className={accent ? "text-[#d4af37]" : "text-[#a39797]"} />}
      <div className="text-[9px] tracking-[0.2em] text-[#a39797]">{label}</div>
    </div>
    <div className={`font-display text-2xl ${accent ? "text-[#d4af37]" : "text-[#f4f0ec]"}`}>{value}</div>
  </div>
);

export default Society;
